// src/api/axiosInstance.ts
import axios from 'axios';
import { refreshToken } from './authService';
import toast from 'react-hot-toast';
import { executeLogout } from '../utils/authUtils';
import { isFlexyStockStatusRejection, shouldLogoutForApiError } from '../utils/sessionAuthError';

const BASE_URL = process.env.REACT_APP_API_URL;

if (!BASE_URL) {
  throw new Error('REACT_APP_API_URL no está definida en las variables de entorno');
}
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Interceptor para agregar el token de autorización y x-api-key
axiosInstance.interceptors.request.use(
  config => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    const url = config.url || ''
    if (url.includes('/orders') || url.includes('/recaudos') || url.includes('/erp-api/ofima')) {
      config.headers['x-api-key'] = 'sk_1234567890abcdefghijk_secret_key';
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // 401 de sesión inválida. Un rechazo por stock Flexy también llega en 401 y no cierra la sesión.
    if (error.response?.status === 401) {
      if (shouldLogoutForApiError(401, error.response?.data)) {
        console.log('Error 401 de sesión');
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
          position: 'top-center',
          duration: 4000,
        });
        executeLogout();
      }
      return Promise.reject(error);
    }

    // Manejar error de validación de sesión (500 con SESSION_VALIDATION_ERROR)
    if (error.response?.status === 500 && shouldLogoutForApiError(500, error.response?.data)) {
      console.log('Error de validación de sesión detectado');
      toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
        position: 'top-center',
        duration: 4000,
      });
      executeLogout();
      return Promise.reject(error);
    }
    
    // Manejar error 403
    if (error.response?.status === 403) {
      if (isFlexyStockStatusRejection(error.response?.data)) {
        return Promise.reject(error);
      }

      const errorMessage = error.response.data?.message || 'No tienes permisos para realizar esta acción';
      
      // Solo limpiar tokens y redirigir si es un error de autenticación (token inválido)
      // No redirigir si es solo un problema de permisos
      if (
        errorMessage.includes('token') ||
        errorMessage.includes('authentication') ||
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('expired')
      ) {
        console.log('Error 403 de autenticación detectado');
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
          position: 'top-center',
          duration: 4000,
        });
        executeLogout();
        return Promise.reject(error);
      }
      
      // Si es solo un problema de permisos, no hacer logout
      // El error se manejará en el componente que hizo la petición
    }

    // Intentar refrescar el token si es 403 y no se ha intentado antes
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshTokenValue = localStorage.getItem('refreshToken');
        if (refreshTokenValue) {
          const newAccessToken = await refreshToken(refreshTokenValue);
          localStorage.setItem('accessToken', newAccessToken);
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (err) {
        console.error('Error refrescando el token:', err);
        // Si el refresh falla, hacer logout
        executeLogout();
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
